/**
 * Route-level ErrorBoundary helper. Fires TWO captures whenever a
 * route boundary mounts with an error:
 *
 *   1. `posthog.captureException()` for the dashboard's exception
 *      stream and the alert pipeline.
 *   2. `POST /api/admin-notify` for direct admin notification with
 *      a reference ID we surface to the user.
 *
 * The two channels are deliberate. PostHog has ingestion lag and
 * (for end-users) ad blockers; the admin-notify route runs on our
 * own infra. Belt-and-braces.
 *
 * Returns `{ referenceId }` so the boundary UI can render it for the
 * user ("Tell us about this and quote ref: ...").
 *
 * Usage:
 *   export function ErrorBoundary() {
 *     const { referenceId } = useReportRouteError("expenses");
 *     return <Oops referenceId={referenceId} />;
 *   }
 */
import { useEffect, useState } from "react";
import { isRouteErrorResponse, useLocation, useRouteError } from "react-router";
import { reportError } from "./analytics";

export interface UseReportRouteErrorResult {
  /** Server-issued admin-notification row id. May be null briefly
   *  on first render and on failure to reach the notify endpoint. */
  referenceId: string | null;
}

export function useReportRouteError(
  routeName: string,
): UseReportRouteErrorResult {
  const error = useRouteError();
  const location = useLocation();
  const [referenceId, setReferenceId] = useState<string | null>(null);

  useEffect(() => {
    if (!error) return;

    const isResponse = isRouteErrorResponse(error);
    const status = isResponse ? error.status : null;

    // Skip routine HTTP statuses that aren't bugs.
    const skipStatus = status === 401 || status === 403 || status === 404;

    const routeError = isResponse
      ? new Error(`Route error ${error.status} ${error.statusText}`)
      : error instanceof Error
        ? error
        : new Error(typeof error === "string" ? error : "Unknown route error");

    // 1. PostHog (always, even for skipped statuses -- the trend is
    //    still useful even if we don't page Barry).
    reportError(routeError, {
      $exception_source: "route_error_boundary",
      route_name: routeName,
      pathname: location.pathname,
      search: location.search,
      route_error_status: status,
      route_error_data: isResponse ? error.data : null,
    });

    // 2. Direct admin-notify -- only for real errors. Public-route
    //    crashes don't ping the phone either.
    if (skipStatus || location.pathname.startsWith("/_public")) return;

    const title = routeError.message.slice(0, 200);
    const dedupeKey = `route_error::${routeName}::${title}`;
    const body = JSON.stringify(
      {
        message: routeError.message,
        stack: routeError.stack?.split("\n").slice(0, 12).join("\n"),
        pathname: location.pathname,
        search: location.search,
        status,
      },
      null,
      2,
    ).slice(0, 3500);

    fetch("/api/admin-notify", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        kind: "route_error",
        severity: "error",
        title: `${routeName}: ${title}`,
        body,
        linkTo: `${location.pathname}${location.search}`,
        dedupeKey,
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) return;
        const data = await resp.json().catch(() => null);
        if (data?.ok && typeof data.id === "string") {
          setReferenceId(data.id);
        }
      })
      .catch(() => {
        // Network failure: PostHog still gets it via path #1.
      });
  }, [error, routeName, location.pathname, location.search]);

  return { referenceId };
}
